/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Collapse, Dropdown, Grid, Menu, Popconfirm, Row, Space, Table, Tooltip, Typography } from 'antd';
import { BookOutlined, CopyOutlined, FolderViewOutlined, HighlightOutlined, MessageOutlined, SmileOutlined } from '@ant-design/icons';
import ActivityTypeTag from '../UI/ActivityTypeTag';
import AutomatedAnswerForm from '../Forms/AutomatedAnswerForm';
import AutomatedAnswersImportModal from '../Modals/AutomatedAnswersImportModal';
import BulkActionsCloneModal from '../Modals/BulkActionsCloneModal';
import BulkActionsModal from '../../components/Modals/BulkActionsModal';
import DeleteWithReferencesModal from '../../components/Modals/DeleteWithReferencesModal';
import MainActivityButton from '../UI/MainActivityButton';
import Permissioned from '../Permissioned';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import TestAutomatedAnswersPanel from '../UI/TestAutomatedAnswersPanel';
import { DecisionIcon } from '../Icons/DecisionIcon';
import { InputIcon } from '../Icons/InputIcon';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { ACTIVITY_TYPES, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE, PERMISSIONS_GROUP_EDITOR, SEARCH_ACTIVITY_TYPES } from '../../constants/main';
import { removeMarkdown } from '../../utils/string/removeMarkdown';
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import { useCreateAutomatedAnswersMutation, useDeleteAutomatedAnswerMutation, useDeleteAutomatedAnswersMutation, useFetchAutomatedAnswersQuery, useUpdateAutomatedAnswerMutation } from '../../store/automatedAnswers/automatedAnswersApi';
import { useFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { generateIntroNodeId } from '../../utils/transform/automatedAnswer';
import { NAV_BOT, NAV_ORGANIZATION_KNOWLEDGE_BASES } from '../../constants/navigation';
import { truncateMarkdown } from '../../utils/string/truncateMarkdown';
import { useReferenceChecker } from '../../utils/hooks/useReferenceChecker';
import { isEmail } from '../../utils/is/isEmail';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import is from '../../utils/is';
import useTheme from '../../utils/hooks/useTheme';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';
import ActivitiesSearchByInput from '../UI/ActivitiesSearchByInput';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import exportAutomatedAnswers from '../../utils/misc/exportAutomatedAnswers';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;
const BULK_ACTIONS = {
  DELETE: 'delete',
  CLONE: 'clone',
  EXPORT: 'export'
};

const AutomatedAnswersTable = () => {
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const isXlSizeOrLess = (screens.xs || screens.sm || screens.md || screens.lg || screens.xl) && !screens.xxl;
  const { primaryBotLanguage, isEditingPrimaryLanguage, currentBotLanguage } = useBotLanguage();
  const { handleMouseDown, isDragging } = useDragDetection();
  const { t } = useTranslation();
  const { messageNodeIconColor, jumpNodeIconColor, defaultNodeIconColor } = useTheme();

  const { data: automatedAnswers, isLoading: isLoadingAutomatedAnswers } = useFetchAutomatedAnswersQuery({ orgId, tenantId, currentBotLanguage });
  const [, { isLoading: isUpdatingAutomatedAnswer }] = useUpdateAutomatedAnswerMutation({ fixedCacheKey: 'update-automated-answer-main-button-shared' });
  const [createAutomatedAnswers, { isLoading: isCreatingAutomatedAnswers }] = useCreateAutomatedAnswersMutation({ fixedCacheKey: 'create-automated-answers-shared' });
  const [deleteAutomatedAnswerByName, { isLoading: isDeletingAutomatedAnswer }] = useDeleteAutomatedAnswerMutation({ fixedCacheKey: 'delete-automated-answer-main-button-shared' });
  const { isFetching: isLoadingNlpPipelines } = useFetchNlpPipelinesQuery({ orgId, tenantId, currentBotLanguage }, { skip: !userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) });
  const [deleteAutomatedAnswers, { isLoading: isDeletingAutomatedAnswers }] = useDeleteAutomatedAnswersMutation();
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);

  const [filteredInfo, setFilteredInfo] = useState({ search: '', type: '' });
  const [tableDataSource, setTableDataSource] = useState([]);
  const [sortedInfo, setSortedInfo] = useState({ order: 'ascend', columnKey: 'name' });
  const [selectedAA, setSelectedAA] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isTestPanelVisible, setIsTestPanelVisible] = useState(false);
  const [isMarkdownVisible, setIsMarkdownVisible] = useState(true);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [selectedAAIdToDelete, setSelectedAAIdToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const availableGroups = [...new Set(automatedAnswers?.items?.map(a => a.group[currentBotLanguage]))];

  const [selectedAutomatedAnswers, setSelectedAutomatedAnswers] = useState([]);
  const [automatedAnswersToShow, setAutomatedAnswersToShow] = useState(0);
  const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);
  const [isCloneAutomatedAnswersModalOpen, setIsCloneAutomatedAnswersModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState();
  const [selectedAutomatedAnswersObjects, setSelectedAutomatedAnswersObjects] = useState([]);

  const hasEditAccess = userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles, false);

  const { resetReferences, relatedIds, checkForAAReferences, isLoading: isFindingReferences } = useReferenceChecker();

  const columns = [
    {
      title: t('Name/Title'),
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name[currentBotLanguage]?.localeCompare(b.name[currentBotLanguage]),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: record => {
        return record.name[currentBotLanguage] ? (
          <Row justify="middle" wrap={false}>
            <Tooltip placement="right" overlayStyle={{ maxWidth: 400 }} title={record.id}>
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={filteredInfo.type === 'name' ? [filteredInfo.search] : []}
                autoEscape
                className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
                textToHighlight={record.name[currentBotLanguage]}
              />
            </Tooltip>

            {
              // If there are other search results within the nested values, show an indicator
              !record.name[currentBotLanguage]?.includes(filteredInfo.search) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view')}>
                  <HighlightOutlined className="text-primary pl-2" />
                </Tooltip>
              ) : null
            }
          </Row>
        ) : <Text type="secondary">{t('(no name)')}</Text>;
      }
    },
    {
      key: `group.${currentBotLanguage}`,
      title: t('Group'),
      hidden: isTestPanelVisible,
      responsive: ['md'],
      filters: (availableGroups || [])
        .filter(group => group)
        .filter((group, index, self) => self.findIndex(g => g === group) === index) // keep unique values
        .sort((a, b) => a.localeCompare(b))
        .map(group => ({
          text: group,
          value: group
        })),
      onFilter: (value, record) => record.group[currentBotLanguage] === value,
      filterIcon: useTableFilterIcon,
      render: record => {
        return record.group[currentBotLanguage] ? (
          <Row justify="middle" wrap={false}>
            <Text
              ellipsis={{
                tooltip: {
                  title: record.group[currentBotLanguage],
                  placement: 'right',
                  overlayStyle: { maxWidth: 400 }
                }
              }}
            >
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[filteredInfo.search]}
                autoEscape
                className="text-overflow-ellipsis overflow-hidden white-space-nowrap"
                textToHighlight={record.group[currentBotLanguage]}
              />
            </Text>
          </Row>
        ) : '';
      }
    },
    {
      key: 'type',
      title: t('Type'),
      hidden: isTestPanelVisible,
      responsive: ['md'],
      width: 110,
      filters: [ACTIVITY_TYPES.AUTOMATED_ANSWERS, ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE].map(type => ({
        text: type === ACTIVITY_TYPES.AUTOMATED_ANSWERS ? t('Intent') : t('Article'),
        value: type
      })),
      onFilter: (value, record) => record.type === value,
      filterIcon: useTableFilterIcon,
      render: record => <ActivityTypeTag type={record.type} />
    },
    {
      title: t('Updated at'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      responsive: ['md'],
      sorter: (a, b) => a.updatedAt - b.updatedAt,
      sortOrder: sortedInfo.columnKey === 'updatedAt' && sortedInfo.order,
      render: updatedAt => {
        if (updatedAt) {
          return moment(updatedAt).format(DEFAULT_DATETIME_FORMAT);
        } else {
          return '-';
        }
      }
    },
    {
      title: t('Agent Input'),
      key: `nlpTrainingData.${currentBotLanguage}.examples`,
      responsive: ['xl'],
      ellipsis: true,
      render: record => {
        const chatbotInput = record?.nlpTrainingData?.examples?.[currentBotLanguage] || [];
        const textToDisplay = !is.emptyArray(chatbotInput) ? chatbotInput[0] : '';

        return textToDisplay ? (
          <Space size={4}>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[filteredInfo.search]}
              autoEscape
              className="d-block text-overflow-ellipsis overflow-hidden white-space-nowrap"
              style={{ maxWidth: 220 }}
              textToHighlight={textToDisplay}
            />

            {
              chatbotInput.length > 1 ? (
                <ResponsiveTagsList
                  maxTagCount={0}
                  values={chatbotInput
                    .slice(1) // Skip the first element since it's already displayed
                    .map(phrase => ({
                      label: phrase,
                      value: phrase,
                      icon: <InputIcon className="text-primary fs-lg v-align-sub" />
                    }))}
                />
              ) : null
            }

            {
              // If there are other search results within the nested values, show an indicator
              !textToDisplay.includes(filteredInfo.search) && chatbotInput.some(trainingPhrase => trainingPhrase.includes(filteredInfo.search)) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        ) : (
          <span className="text-secondary">
            -
          </span>
        );
      }
    },
    {
      title: t('Agent Responses'),
      key: 'responseOptions',
      responsive: isTestPanelVisible ? ['xxl'] : ['lg'],
      className: 'word-break-break-all',
      render: record => {
        let sourceList = record.content.nodes.map(node => {
          switch (node.type) {
            case NODE_TYPES.MESSAGE:
              return {
                label: node.label[currentBotLanguage] || '',
                type: node.type
              };
            case NODE_TYPES.JUMP:
            case NODE_TYPES.USER_FEEDBACK:
              return {
                label: node.jumpTo,
                type: node.type
              };
            case NODE_TYPES.KNOWLEDGE_BASE_ARTICLE:
              return {
                label: t('Article body'),
                type: node.type
              };
            default:
              return null;
          }
        }).filter(n => n) || [];

        const textToDisplay = !is.emptyArray(sourceList) ? sourceList[0].label : '';
        return !is.emptyArray(sourceList) ? (
          <Space>
            {renderResponseTypeTags(record)}

            {
              isMarkdownVisible ? (
                <Text
                  className="line-clamp"
                >
                  <ReactMarkdown
                    skipHtml
                    linkTarget="_blank"
                  >
                    {
                      truncateMarkdown(textToDisplay, {
                        limit: 150,
                        ellipsis: true,
                        replaceImages: true,
                        replaceBlankSpaces: true
                      })
                    }
                  </ReactMarkdown>
                </Text>
              ) : (
                <Highlighter
                  highlightClassName="bg-highlight p-0"
                  searchWords={[filteredInfo.search]}
                  autoEscape
                  className="d-block text-overflow-ellipsis overflow-hidden white-space-nowrap m-0 word-break-normal"
                  style={{ maxWidth: 220 }}
                  textToHighlight={removeMarkdown(truncateMarkdown(textToDisplay, {
                    limit: 150,
                    ellipsis: true,
                    replaceImages: true,
                    replaceBlankSpaces: true
                  }))}
                />
              )
            }

            {
              sourceList.length > 1 ? (
                <ResponsiveTagsList
                  maxTagCount={0}
                  values={sourceList
                    .slice(1) // Skip the first element since it's already displayed
                    .map(response => ({
                      label: (
                        <ReactMarkdown
                          skipHtml
                          linkTarget="_blank"
                        >
                          {response.label}
                        </ReactMarkdown>
                      ),
                      value: response.label,
                      icon: response.type === NODE_TYPES.MESSAGE ? (
                        <MessageOutlined style={{ color: messageNodeIconColor }} />
                      ) : response.type === NODE_TYPES.JUMP ? (
                        <DecisionIcon style={{ color: jumpNodeIconColor }} />
                      ) : response.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE ? (
                        <BookOutlined style={{ color: defaultNodeIconColor }} />
                      ) : (
                        <SmileOutlined style={{ color: defaultNodeIconColor }} />
                      )
                    }))
                  }
                />
              ) : null
            }

            {
              // If there are other search results within the nested values, show an indicator
              !textToDisplay.includes(filteredInfo.search) && sourceList.some(node => node.label.includes(filteredInfo.search)) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        ) : (
          <span className="text-secondary">
            {t('(no response options)')}
          </span>
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 160,
      render: record => (
        <Space onClick={(e) => e.stopPropagation()} size={4}>
          {
            userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) ? (
              <Fragment>
                <Tooltip overlayClassName="pointer-events-none" title={t('Edit')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<EditIcon />}
                    onClick={() => setSelectedAA(record)}
                  />
                </Tooltip>

                <Tooltip
                  overlayClassName="pointer-events-none"
                  title={
                    record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE
                      ? <Typography.Text>{t('KB articles cannot be cloned')}</Typography.Text>
                      : t('Clone')
                  }
                >
                  <Button
                    shape="circle"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => handleSingleCloneModal(record)}
                    disabled={!isEditingPrimaryLanguage || record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE}
                  />
                </Tooltip>

                <Popconfirm
                  title={t('Delete this automated answer?')}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  placement="topRight"
                  onConfirm={() => checkForReferencesAndDelete(record.id)}
                  zIndex={1100}
                  disabled={!isEditingPrimaryLanguage || record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE}
                >
                  <Tooltip
                    overlayClassName="pointer-events-none"
                    title={
                      record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE
                        ? <Typography.Text>{t('To delete KB articles click ')}<Link to={replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASES, { orgId })}>{t('here')}</Link></Typography.Text>
                        : t('Delete')
                    }
                  >
                    <Button
                      shape="circle"
                      type="text"
                      icon={<DeleteIcon />}
                      disabled={!isEditingPrimaryLanguage || record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE}
                      loading={(isDeletingAutomatedAnswer && selectedAAIdToDelete.includes(record.id)) || (isDeletingAutomatedAnswers && selectedAutomatedAnswers.includes(record.id))}
                    />
                  </Tooltip>
                </Popconfirm>

                <MainActivityButton
                  buttonType="text"
                  chaptersource={record}
                  disabled={!isEditingPrimaryLanguage}
                  botId={tenantId}
                />
              </Fragment>
            ) : (
              <Tooltip overlayClassName="pointer-events-none" title={t('View automated answer')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<FolderViewOutlined/>}
                  loading={isLoadingAutomatedAnswers || isUpdatingAutomatedAnswer || isCreatingAutomatedAnswers || isDeletingAutomatedAnswers}
                  onClick={() => setSelectedAA(record)}
                />
              </Tooltip>
            )
          }
        </Space>
      )
    }
  ].filter(column => !column.hidden);

  // When a search value is entered markdown is replaced with plain text
  useEffect(() => {
    if (filteredInfo.search && isEmail(filteredInfo.search)) {
      setIsMarkdownVisible(false);
    } else {
      setIsMarkdownVisible(true);
    }
  }, [filteredInfo.search]);

  useEffect(() => {
    if (!automatedAnswers) {
      return;
    }
    const urlSearchParams = new URLSearchParams(location.search);
    const aaToEdit = urlSearchParams.get('edit');
    if (aaToEdit) {
      setSelectedAA(automatedAnswers.items.find(automatedAnswer => automatedAnswer.id === aaToEdit) ?? {});
    } else {
      setSelectedAA(null);
    }
  }, [location.search, automatedAnswers]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    if (selectedAA) {
      urlSearchParams.set('edit', selectedAA.id ?? 'new');
    } else {
      urlSearchParams.delete('edit');
    }
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    });
  }, [selectedAA]);

  const onDropdownSelect = (key) => {
    let newSelectedAutomatedAnswers = [];
    if (key === 'all') {
      newSelectedAutomatedAnswers = automatedAnswers?.items
        ?.filter(aa => aa.type !== ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE)
        .map((item) => item.id);
    }
    setSelectedAutomatedAnswers(newSelectedAutomatedAnswers);
  };

  const onRowSelectChange = (newSelectedAutomatedAnswers) => {
    setSelectedAutomatedAnswers(newSelectedAutomatedAnswers);
  };

  const handleBulkActionsMenuClick = (e) => {
    if (e.key === BULK_ACTIONS.CLONE) {
      openBulkActionsCloneActivitiesModal();
    } else if (e.key === BULK_ACTIONS.DELETE) {
      openBulkActionsModal();
    } else if (e.key === BULK_ACTIONS.EXPORT) {
      const automatedAnswersToExport = selectedAutomatedAnswers?.map((selectedAutomatedAnswer) => {
        return automatedAnswers?.items?.find(item => item.id === selectedAutomatedAnswer);
      });
      exportAutomatedAnswers(automatedAnswersToExport, primaryBotLanguage);
    }
    setBulkAction(e.key);
  };

  //Bulk Actions Modal Handlers
  const openBulkActionsCloneActivitiesModal = () => {
    setSelectedAutomatedAnswersObjects(bulkActionsCloneAutomatedAnswersModalData()); // Set up automated answers object to be cloned
    setAutomatedAnswersToShow(selectedAutomatedAnswers.length > 5 ? 5: selectedAutomatedAnswers.length);
    setIsCloneAutomatedAnswersModalOpen(true);
  };

  const closeBulkActionsCloneAutomatedAnswersModal = () => {
    setIsCloneAutomatedAnswersModalOpen(false);
  };

  const onBulkActionsCloneAutomatedAnswersModalShowMore = () => {
    setAutomatedAnswersToShow(automatedAnswersToShow + 5);
  };

  const bulkActionsCloneAutomatedAnswersModalData = () => {
    const automatedAnswersToClone = selectedAutomatedAnswers?.map((selectedAutomatedAnswer) => {
      return automatedAnswers?.items?.find(item => item.id === selectedAutomatedAnswer);
    });
    return automatedAnswersToClone;
  };

  const openBulkActionsModal = () => {
    setAutomatedAnswersToShow(selectedAutomatedAnswers.length > 5 ? 5 : selectedAutomatedAnswers.length);
    setIsBulkActionsModalOpen(true);
  };

  const closeBulkActionsModal = () => {
    setIsBulkActionsModalOpen(false);
  };

  const deleteBulkAction = async () => {
    const isBulkActionSuccessful = await deleteAutomatedAnswers({
      orgId,
      tenantId,
      data: selectedAutomatedAnswers
    }).unwrap();

    if (isBulkActionSuccessful) {
      setSelectedAutomatedAnswers([]);
      setIsBulkActionsModalOpen(false);
      setShowReferenceModal(false);
    }
  };

  const handleBulkActions = async () => {
    let hasReferencesRes;
    let isBulkActionSuccessful;
    switch(bulkAction) {
      case BULK_ACTIONS.DELETE:
        hasReferencesRes = await checkForAAReferences(selectedAutomatedAnswers);
        if(!hasReferencesRes) {
          deleteBulkAction();
        } else {
          setShowReferenceModal(true);
        }
        break;
      default:
        break;
    }

    if (isBulkActionSuccessful) {
      setSelectedAutomatedAnswers([]);
      setIsBulkActionsModalOpen(false);
    }
  };

  const onBulkActionsModalShowMore = () => {
    setAutomatedAnswersToShow(automatedAnswersToShow + 5);
  };

  const bulkActionsModalData = () => {
    const automatedAnswersToDelete = selectedAutomatedAnswers?.map((selectedAutomatedAnswer) => {
      return automatedAnswers?.items?.find(aa => aa.id === selectedAutomatedAnswer);
    });
    return automatedAnswersToDelete;
  };

  const handleSingleCloneModal = (record) => {
    // Find the respective automatedAnswer based on the record of the table clicked
    const automatedAnswerToClone = [automatedAnswers?.items?.find(aa => aa.id === record.id)];

    setSelectedAutomatedAnswersObjects(automatedAnswerToClone);
    setAutomatedAnswersToShow(1);
    setIsCloneAutomatedAnswersModalOpen(true);
  };

  const handleCloneSubmission = async (values) => {
    const payload = selectedAutomatedAnswersObjects.map(automatedAnswerToClone => {
      const oldIntroNodeId = automatedAnswerToClone.content.nodes[0].id;
      let newAutomatedAnswer = { ...automatedAnswerToClone };
      const newAutomatedAnswerId = uuidv4();
      newAutomatedAnswer.id = newAutomatedAnswerId;
      newAutomatedAnswer.name = {
        [primaryBotLanguage]: `${automatedAnswerToClone.name[primaryBotLanguage]} (Copy)`
      };

      if (selectedAutomatedAnswersObjects.length === 1 ) {
        newAutomatedAnswer.name[primaryBotLanguage] = values.newName;
      } else {
        newAutomatedAnswer.name[primaryBotLanguage] = `${automatedAnswerToClone.name[primaryBotLanguage]} (Copy)`;
      }

      // We need to update IDs for every node, and also the respective edges
      newAutomatedAnswer.content.nodes.forEach((node) => {
        const oldNodeId = node.id;
        const newNodeId = node.type === NODE_TYPES.INTRO ? generateIntroNodeId(newAutomatedAnswer.id, newAutomatedAnswer.name[primaryBotLanguage]) : uuidv4();

        newAutomatedAnswer.content = {
          ...newAutomatedAnswer.content,
          // Update node IDs
          nodes: newAutomatedAnswer.content.nodes.map(n => {
            if (n.id === oldNodeId) {
              return { ...n, id: newNodeId };
              // Make sure jump nodes don't point to the parent automatedAnswer, but to the new one
            } else if ((n.type === NODE_TYPES.JUMP || n.type === NODE_TYPES.USER_FEEDBACK) && n.jumpTo === oldIntroNodeId) {
              return { ...n, label: { [primaryBotLanguage]: '' }, jumpTo: newNodeId };
            } else {
              return n;
            }
          }),
          // Update edge sources and targets
          edges: newAutomatedAnswer.content?.edges?.map(e => {
            if (e.source === oldNodeId) {
              return { ...e, source: newNodeId };
            } else if (e.target === oldNodeId) {
              return { ...e, target: newNodeId };
            } else {
              return e;
            }
          }),
          // Update nlpTrainingData's ID to match the new AA's ID
          nlpTrainingData: {
            ...automatedAnswerToClone.nlpTrainingData,
            id: newAutomatedAnswerId
          }
        };
        if (automatedAnswerToClone.content.settings && !is.emptyObject(automatedAnswerToClone.content.settings)) {
          newAutomatedAnswer.content.settings = { ...automatedAnswerToClone.content.settings };
        }
      });
      return newAutomatedAnswer;
    });

    const isSaveSuccessful = await createAutomatedAnswers({ orgId, tenantId: values.tenantId, data: payload }).unwrap();

    if (isSaveSuccessful) {
      openConsoleAlertMessage({
        type: 'success',
        message: selectedAutomatedAnswersObjects.length > 1
          ? t('Automated answers cloned!')
          : t('Automated answer cloned!'),
        duration: 10,
        ...(values.tenantId !== tenantId && {
          secondaryButton: {
            action: () => {
              navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: values.tenantId })}?view=behavior&tab=automated-answers`);
            },
            title: t('View')
          }
        })
      });

      // Trigger callback to notify parent component of success
      closeBulkActionsCloneAutomatedAnswersModal();
    } else {
      openConsoleAlertMessage({ type: 'error', message: t('There was an error cloning the automated answers.') });
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedAutomatedAnswers,
    onChange: onRowSelectChange,
    getCheckboxProps: (record) => ({
      disabled: record.type === ACTIVITY_TYPES.KNOWLEDGE_BASE_ARTICLE
    }),
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => onDropdownSelect('all')
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => onDropdownSelect('none')
      }
    ]
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    setSortedInfo(sorter);
    setPageSize(pagination.pageSize);
  };

  const openImportModal = () => {
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
  };

  const toggleTestPanel = () => {
    setIsTestPanelVisible(!isTestPanelVisible);
  };

  /**
   * Proceed to actual deletion of AA after checking for references in chaptersources
   * @param {String} automatedAnswerId the AA to be deleted
   */
  const checkForReferencesAndDelete = async (automatedAnswerId) => {
    setSelectedAAIdToDelete(automatedAnswerId);
    const res = await checkForAAReferences(automatedAnswerId);

    if(!res) {
      deleteAA(automatedAnswerId);
    } else {
      setShowReferenceModal(true);
    }
  };

  /**
   * Make the appropriate api call to delete an AA .
   * @param {*} automatedAnswerId the ID of the AA to be deleted
   */
  const deleteAA = async(automatedAnswerId) => {
    const isDeleted = await deleteAutomatedAnswerByName({ orgId, tenantId, automatedAnswerId }).unwrap();
    // If delete was successful, refresh the list with the latest data
    if (isDeleted) {
      if (selectedAutomatedAnswers.includes(selectedAAIdToDelete)) {
        const newSelectedAutomatedAnswers = [...selectedAutomatedAnswers];
        const reqIndex = newSelectedAutomatedAnswers.indexOf(selectedAAIdToDelete);
        newSelectedAutomatedAnswers.splice(reqIndex, 1);
        setSelectedAutomatedAnswers(newSelectedAutomatedAnswers);
      }
    }
    resetReferences();
    setSelectedAAIdToDelete(null);
    setShowReferenceModal(false);
  };

  const renderResponseTypeTags = (chaptersource) => {
    return (
      <Fragment>
        {
          chaptersource.content.nodes.some(node => node.type === NODE_TYPES.MESSAGE) ? (
            <Tooltip overlayClassName="pointer-events-none" title={t('Contains messages')}>
              <MessageOutlined style={{ color: messageNodeIconColor }} />
            </Tooltip>
          ) : null
        }

        {
          chaptersource.content.nodes.some(node => node.type === NODE_TYPES.JUMP) ? (
            <Tooltip overlayClassName="pointer-events-none" title={t('Contains a redirect to an Automated Answer or Flow')}>
              <DecisionIcon style={{ color: jumpNodeIconColor }} />
            </Tooltip>
          ) : null
        }

        {
          chaptersource.content.nodes.some(node => node.type === NODE_TYPES.USER_FEEDBACK) ? (
            <Tooltip overlayClassName="pointer-events-none" title={t('Contains a user feedback flow')}>
              <SmileOutlined style={{ color: defaultNodeIconColor }} />
            </Tooltip>
          ) : null
        }

        {
          chaptersource.content.nodes.some(node => node.type === NODE_TYPES.KNOWLEDGE_BASE_ARTICLE) ? (
            <Tooltip overlayClassName="pointer-events-none" title={t('Contains a knowledge base article')}>
              <BookOutlined style={{ color: defaultNodeIconColor }} />
            </Tooltip>
          ) : null
        }
      </Fragment>
    );
  };

  const onReferencesModalCancel = () => {
    setShowReferenceModal(false);
    resetReferences();
  };

  return (
    <Fragment>
      <Row gutter={[16, 16]} wrap={false}>
        <Col flex="auto">
          <Collapse
            ghost
            accordion
            activeKey={selectedAA ? 'edit' : 'table'}
            destroyInactivePanel
          >
            <Collapse.Panel
              key="table"
              showArrow={false}
              className="ant-collapse-p-0"
              forceRender
            >
              <Row gutter={[16, 16]} className="mb-4">
                <Col flex="auto">
                  <Space className="flex-wrap mb-2">
                    <ActivitiesSearchByInput
                      items = {automatedAnswers?.items || []}
                      onFilteredItemsReturn = {(filteredItems, searchValue, searchByType) => {
                        setFilteredInfo({
                          type: searchByType,
                          search: searchValue
                        });

                        setTableDataSource(filteredItems.filter(a => a.id !== DEFAULT_FALLBACK_ID)); // Do not display default fallbacks (default fallbacks are also automated answers));
                      }}
                      activityType = {SEARCH_ACTIVITY_TYPES.AUTOMATED_ANSWERS}
                    />
                  </Space>
                </Col>

                <Col flex="auto" className="text-right">
                  <Space
                    className={classNames('flex-wrap', { 'w-100 ant-space-stretch': isXlSizeOrLess })}
                  >
                    <Permissioned
                      allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                      currentPermissions={userPermissions}
                      fallbackComponent={null}
                    >
                      <Dropdown
                        trigger={['click']}
                        overlay={
                          <Menu
                            onClick={handleBulkActionsMenuClick}
                            items={[
                              {
                                label: t('Clone Selected'),
                                key: BULK_ACTIONS.CLONE
                              },
                              {
                                label: t('Delete Selected'),
                                key: BULK_ACTIONS.DELETE
                              },
                              {
                                label: t('Export Selected'),
                                key: BULK_ACTIONS.EXPORT
                              }
                            ]}
                          />
                        }
                        disabled={!selectedAutomatedAnswers.length > 0}
                      >
                        <Button
                          block
                          size="large"
                        >
                          {t('Bulk Actions')}
                          <ArrowDownIcon />
                        </Button>
                      </Dropdown>
                    </Permissioned>

                    <Permissioned
                      allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                      currentPermissions={userPermissions}
                      fallbackComponent={null}
                    >
                      <Button
                        size="large"
                        block={isXlSizeOrLess}
                        onClick={toggleTestPanel}
                        disabled={isLoadingNlpPipelines}
                      >
                        {t('Test Responses')}
                      </Button>
                    </Permissioned>

                    <Permissioned
                      allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                      currentPermissions={userPermissions}
                      fallbackComponent={null}
                    >
                      <Button
                        size="large"
                        block={isXlSizeOrLess}
                        onClick={openImportModal}
                        disabled={isUpdatingAutomatedAnswer}
                      >
                        {t('Import / Export')}
                      </Button>
                    </Permissioned>

                    <Permissioned
                      allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                      currentPermissions={userPermissions}
                      fallbackComponent={null}
                    >
                      <Button
                        type="primary"
                        size="large"
                        block={isXlSizeOrLess}
                        icon={<PlusIcon />}
                        disabled={!isEditingPrimaryLanguage || isUpdatingAutomatedAnswer}
                        onClick={() => setSelectedAA({})}
                      >
                        {t('Add Intent')}
                      </Button>
                    </Permissioned>
                  </Space>
                </Col>
              </Row>

              <Row gutter={8} align="top" wrap={false}>
                <Col flex="auto">
                  <Table
                    rowSelection={hasEditAccess ? rowSelection : null}
                    onRow={(record) => ({
                      onMouseDown: handleMouseDown,
                      onClick: () => {
                        if(!isDragging() && userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))) {
                          setSelectedAA(record);
                        }
                      }
                    })}
                    rowClassName="cursor-pointer"
                    rowKey="id"
                    size="small"
                    columns={columns}
                    pagination={{
                      size: 'default',
                      pageSize: pageSize,
                      showSizeChanger: true,
                      showTotal: (total) => `${t('Total Automated Answers')}: ${total}`
                    }}
                    dataSource={tableDataSource}
                    loading={isLoadingAutomatedAnswers}
                    onChange={handleTableChange}
                  />
                </Col>

                {
                  isTestPanelVisible ? (
                    <Col flex="0 0 420px">
                      <Permissioned
                        allowedPermissions={PERMISSIONS_GROUP_EDITOR}
                        currentPermissions={userPermissions}
                        fallbackComponent={null}
                      >
                        <TestAutomatedAnswersPanel
                          setIsTestPanelVisible={setIsTestPanelVisible}
                          setSelectedAA={setSelectedAA}
                        />
                      </Permissioned>
                    </Col>
                  ) : null
                }
              </Row>

              <AutomatedAnswersImportModal onClose={closeImportModal} isModalOpen={isImportModalOpen} />

              <BulkActionsModal
                title={`${selectedAutomatedAnswers.length === 1 ? 'automated answer' : 'automated answers'}`}
                action={bulkAction}
                isModalOpen={isBulkActionsModalOpen}
                data={bulkActionsModalData()}
                onModalClose={closeBulkActionsModal}
                onModalConfirm={handleBulkActions}
                loader={isLoadingAutomatedAnswers || isDeletingAutomatedAnswers || isFindingReferences}
                onShowMore={onBulkActionsModalShowMore}
                entityCount={automatedAnswersToShow}
                displayProperty={['name', primaryBotLanguage]}
              />

              <BulkActionsCloneModal
                isModalOpen={isCloneAutomatedAnswersModalOpen}
                dataToBeCloned={selectedAutomatedAnswersObjects}
                onModalClose={closeBulkActionsCloneAutomatedAnswersModal}
                loader={isLoadingAutomatedAnswers || isCreatingAutomatedAnswers}
                onShowMore={onBulkActionsCloneAutomatedAnswersModalShowMore}
                cloneEntityCount={automatedAnswersToShow}
                onSubmit={handleCloneSubmission}
                configOverrides= {{
                  modalTitle: 'Clone Automated Answers',
                  tenantIdDescription: 'Select the agent you want to add the cloned automated answers to.',
                  newNameDescription: 'The cloned automated answers name',
                  submitBtnTitle: 'Clone Automated Answers'
                }}
              />
            </Collapse.Panel>

            <Collapse.Panel
              key="edit"
              showArrow={false}
              className="ant-collapse-p-0"
            >
              {
                selectedAA ? (
                  <AutomatedAnswerForm
                    selectedAA={selectedAA}
                    setSelectedAA={setSelectedAA}
                  />
                ) : null
              }
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>

      {/* Modal to see the references of one or more AA to be deleted and proceed or not the the deletion */}
      <DeleteWithReferencesModal
        isModalOpen={showReferenceModal}
        title={selectedAutomatedAnswers.length > 1 ? t('Delete Automated Answers') : t('Delete Automated Answer')}
        description={
          selectedAutomatedAnswers.length > 1
            ? t('The items you are trying to delete are referenced to the following Automated Answers/Flows/Deployments. Are you sure you want to delete it?')
            : t('The item you are trying to delete is referenced to the following Automated Answers/Flows/Deployments. Are you sure you want to delete it?')
        }
        onModalClose={onReferencesModalCancel}
        onModalConfirm={() => {
          if (selectedAutomatedAnswers.length) {
            deleteBulkAction();
          } else {
            deleteAA(selectedAAIdToDelete);
          }
        }}
        loader={isDeletingAutomatedAnswer || isDeletingAutomatedAnswers}
        chapterSourcesData={relatedIds}
      />
    </Fragment>
  );
};

export default AutomatedAnswersTable;
